import { compactPart, DEFAULT_RULES, type INamingRules } from './filing/template.js';
import { DEFAULT_THRESHOLD } from './filing/decide.js';
import type { IClient } from './filing/clients.js';
import type { ILearnedRule } from './filing/learned.js';
import { allCategories, docTypeOf, getPack, isPackId, type PackId } from './packs/index.js';
import { STAGES } from './programs/types.js';

/**
 * One connected document library and everything AI Admin follows for it: the confirmed
 * clients, naming and folder rules, how careful to be, and the fixes it has learned.
 * Settings only: no document content is ever stored.
 */
export interface IWorkspace {
  driveId: string;
  siteId: string;
  siteName: string;
  libraryName: string;
  webUrl: string;
  /** Folder watched for new documents (created during setup). */
  inboxFolderId?: string;
  inboxName: string;
  clients: IClient[];
  rules: INamingRules;
  /** 0–1: a document is filed on its own only when every field is at least this sure. */
  threshold: number;
  learned: ILearnedRule[];
  paused: boolean;
  /** ISO time automatic filing starts again on its own. */
  pausedUntil?: string;
  /** Top-level folders Clean up leaves exactly as they are. */
  excludedFolders: string[];
  setupDone: boolean;
  /**
   * How new documents are filed. 'template': the accounting preset (Client › Year › Category, fields
   * and a naming template). 'folders': any structure: each folder has a purpose and the AI picks one.
   */
  mode: 'template' | 'folders';
  /** 'folders' mode: the folders of the approved structure and what belongs in each. */
  folderMap: { path: string; purpose: string }[];
  /** 'folders' mode: Date_Title ("2025-03-31_Vacation Policy"), compact ("2025-03-31_VacationPolicy"), or keep the name. */
  naming: 'dateTitle' | 'compact' | 'keep';
  /** 'template' mode: which pack's document types and rules apply ('accounting-ca' when unset). */
  packId?: PackId;
  /**
   * Microsoft retention labels the firm created from AI Admin (only on plans that include them):
   * record category id → label name. Set on each filed record as well as the columns.
   */
  retentionLabels?: { [recordCategoryId: string]: string };
  /**
   * Filing in the background (our service, no browser needed): on once the site's admin has given
   * the filing service write access to this site. Off: filing happens while someone has the app open.
   */
  background?: { enabled: boolean; since?: string; by?: string };
  /**
   * Client upload links: on once a site admin has given our service write access to this site (the
   * same access filing in the background uses). Clients' files go straight into the firm's library.
   */
  uploads?: { enabled: boolean; since?: string; by?: string };
  /**
   * The opening of the "upload link" email to clients, as the firm wrote it (plain text, kept when
   * client uploads are turned off and on again). The default text is used when unset.
   */
  uploadIntro?: string;
  /**
   * The firm (release 1): its lawyers and clerks (names and sign-in addresses, used to assign files
   * and sign-offs), the programs it offers (all when unset), and when its lists and folders were set up.
   */
  firm?: IFirm;
  updatedAt?: string;
  updatedBy?: string;
  /** Version for safe saving; set when read, never stored. */
  etag?: string;
}

export interface IStaff {
  name: string;
  email?: string;
  role: 'lawyer' | 'clerk';
}

/** A fee instalment the firm bills when a file reaches a stage (books stay in the firm's accounting). */
export interface IInstalment {
  label: string;
  stage: string;
}

export interface IFirm {
  staff: IStaff[];
  /** Instalments billed at stages, for every file (a reminder only). */
  instalments?: IInstalment[];
  /** Program ids offered; all programs when empty. */
  programs: string[];
  /** When the firm's lists and folders were created (ISO). */
  listsReady?: string;
  /**
   * Restricted files (refugee and private-sponsorship files, and any the firm marks): their own
   * library, lists and history, open only to a "Restricted files" group of the site.
   */
  restricted?: { driveId: string; libraryName: string; ready: string };
}

export const LIMITS = { clients: 2000, aliases: 10, learned: 500, excluded: 50, text: 120, staff: 100, programs: 200 };

export function newWorkspace(driveId: string): IWorkspace {
  return {
    driveId, siteId: '', siteName: '', libraryName: '', webUrl: '', inboxName: 'Inbox',
    clients: [], rules: DEFAULT_RULES, threshold: DEFAULT_THRESHOLD, learned: [], paused: true,
    excludedFolders: [], setupDone: false, mode: 'template', folderMap: [], naming: 'dateTitle', packId: 'on-law-immigration'
  };
}

function text(v: unknown, max: number = LIMITS.text): string {
  return typeof v === 'string' ? v.replace(/[\u0000-\u001f]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, max) : '';
}

function list<T>(v: unknown, max: number, each: (x: unknown) => T | undefined): T[] {
  return (Array.isArray(v) ? v : []).slice(0, max).map(each).filter((x): x is T => x !== undefined);
}

const PARTS: INamingRules['parts'] = ['date', 'client', 'docType', 'detail'];
const FOLDERS: INamingRules['folders'] = ['area', 'client', 'applicant', 'year', 'category'];

function rules(v: unknown): INamingRules {
  const r = (v ?? {}) as Partial<INamingRules>;
  const parts = list(r.parts, 4, p => (PARTS.indexOf(p as never) !== -1 ? p as INamingRules['parts'][number] : undefined));
  const folders = list(r.folders, 3, p => (FOLDERS.indexOf(p as never) !== -1 ? p as INamingRules['folders'][number] : undefined));
  const unique = <T>(xs: T[]): T[] => xs.filter((x, i) => xs.indexOf(x) === i);
  const p = unique(parts);
  const layout = (v: unknown): INamingRules['folders'] | undefined => {
    const f = unique(list(v, 4, x => (FOLDERS.indexOf(x as never) !== -1 ? x as INamingRules['folders'][number] : undefined)));
    // Every layout starts with the client (law: optionally after the practice area), so one
    // client's or matter's documents never mix with another's.
    const start: number = f[0] === 'area' ? 1 : 0;
    return f.indexOf('client') === start ? f : undefined;
  };
  const byCategory: { [category: string]: INamingRules['folders'] } = {};
  const categories: string[] = allCategories();
  for (const [category, value] of Object.entries((r.byCategory ?? {}) as Record<string, unknown>)) {
    const f = layout(value);
    if (categories.indexOf(category) !== -1 && f) {
      byCategory[category] = f;
    }
  }
  // Date, client and type are what make a name findable; a template without them isn't accepted.
  return {
    parts: p.indexOf('date') !== -1 && p.indexOf('client') !== -1 && p.indexOf('docType') !== -1 ? p : DEFAULT_RULES.parts,
    style: r.style === 'spaces' ? 'spaces' : 'underscores',
    folders: layout(folders) ?? DEFAULT_RULES.folders,
    ...(Object.keys(byCategory).length ? { byCategory } : {})
  };
}

function client(v: unknown): IClient | undefined {
  const c = (v ?? {}) as Partial<IClient>;
  const name: string = text(c.name);
  const short: string = compactPart(text(c.short, 40)).slice(0, 40);
  if (!name || !short) {
    return undefined;
  }
  const aliases: string[] = list(c.aliases, LIMITS.aliases, a => text(a) || undefined);
  const out: IClient = aliases.length ? { name, short, aliases } : { name, short };
  // Law matters: number, practice area and lawyer (from the firm's matter list).
  const number: string = text(c.number, 40);
  const area: string = text(c.area, 80);
  const lawyer: string = text(c.lawyer, 120);
  return { ...out, ...(number ? { number } : {}), ...(area ? { area } : {}), ...(lawyer ? { lawyer } : {}) };
}

function learnedRule(v: unknown, packId?: string): ILearnedRule | undefined {
  const r = (v ?? {}) as Partial<ILearnedRule>;
  const id: string = text(r.id, 40);
  if (!id || (r.kind !== 'alias' && r.kind !== 'issuer')) {
    return undefined;
  }
  const out: ILearnedRule = { id, kind: r.kind, createdAt: text(r.createdAt, 40) || new Date().toISOString() };
  if (r.kind === 'alias') {
    out.written = text(r.written);
    out.client = text(r.client, 40);
    if (!out.written || !out.client) {
      return undefined;
    }
  } else {
    out.issuer = text(r.issuer);
    out.docType = text(r.docType, 40);
    if (!out.issuer || !docTypeOf(getPack(packId), out.docType)) {
      return undefined;
    }
    if (text(r.client, 40)) {
      out.client = text(r.client, 40);
    }
  }
  if (text(r.source, 200)) {
    out.source = text(r.source, 200);
  }
  if (text(r.createdBy)) {
    out.createdBy = text(r.createdBy);
  }
  out.used = Math.max(0, Math.floor(Number(r.used) || 0));
  return out;
}

/** Retention label names, only for the pack's own record categories. */
function labels(v: unknown, packId?: string): IWorkspace['retentionLabels'] {
  const ids: string[] = getPack(packId).recordCategories.map(c => c.id);
  const out: { [id: string]: string } = {};
  for (const [id, name] of Object.entries((v && typeof v === 'object' ? v : {}) as Record<string, unknown>)) {
    if (ids.indexOf(id) !== -1 && text(name, 64)) {
      out[id] = text(name, 64);
    }
  }
  return Object.keys(out).length ? out : undefined;
}

/** Cleans a workspace sent by the browser: untrusted input, so every field is bounded and checked. */
export function sanitizeWorkspace(driveId: string, input: unknown): IWorkspace {
  const w = (input ?? {}) as Partial<IWorkspace>;
  const clients: IClient[] = list(w.clients, LIMITS.clients, client);
  const seen: { [short: string]: boolean } = {};
  const threshold: number = Number(w.threshold);
  const pausedUntil: string = text(w.pausedUntil, 40);
  return {
    driveId,
    siteId: text(w.siteId, 300),
    siteName: text(w.siteName, 200),
    libraryName: text(w.libraryName, 200),
    webUrl: /^https:\/\/[^/]+\.sharepoint\.(com|us|de|cn)\//i.test(text(w.webUrl, 500)) ? text(w.webUrl, 500) : '',
    inboxFolderId: text(w.inboxFolderId, 200) || undefined,
    inboxName: text(w.inboxName, 80) || 'Inbox',
    // One client per short name: the short name is what file names are built from.
    clients: clients.filter(c => (seen[c.short.toLowerCase()] ? false : (seen[c.short.toLowerCase()] = true))),
    rules: rules(w.rules),
    threshold: Number.isFinite(threshold) ? Math.min(0.99, Math.max(0.5, threshold)) : DEFAULT_THRESHOLD,
    learned: list(w.learned, LIMITS.learned, r => learnedRule(r, isPackId(w.packId) ? w.packId : undefined)),
    paused: w.paused === true,
    pausedUntil: pausedUntil && !isNaN(Date.parse(pausedUntil)) ? new Date(pausedUntil).toISOString() : undefined,
    excludedFolders: list(w.excludedFolders, LIMITS.excluded, f => text(f, 200) || undefined),
    setupDone: w.setupDone === true,
    mode: w.mode === 'folders' ? 'folders' : 'template',
    folderMap: list(w.folderMap, 300, f => {
      const x = (f ?? {}) as { path?: unknown; purpose?: unknown };
      const path: string = text(x.path, 400).replace(/^\/+|\/+$/g, '');
      return path ? { path, purpose: text(x.purpose, 200) } : undefined;
    }),
    naming: w.naming === 'keep' || w.naming === 'compact' ? w.naming : 'dateTitle',
    packId: isPackId(w.packId) ? w.packId : undefined,
    retentionLabels: labels(w.retentionLabels, isPackId(w.packId) ? w.packId : undefined),
    background: w.background && typeof w.background === 'object' && (w.background as { enabled?: unknown }).enabled === true
      ? { enabled: true, since: text((w.background as { since?: unknown }).since, 40) || undefined, by: text((w.background as { by?: unknown }).by, 200) || undefined }
      : undefined,
    uploads: w.uploads && typeof w.uploads === 'object' && (w.uploads as { enabled?: unknown }).enabled === true
      ? { enabled: true, since: text((w.uploads as { since?: unknown }).since, 40) || undefined, by: text((w.uploads as { by?: unknown }).by, 200) || undefined }
      : undefined,
    uploadIntro: paragraphText(w.uploadIntro, 1500) || undefined,
    firm: firm(w.firm)
  };
}

/** Text with paragraphs kept (line breaks), other control characters removed. */
function paragraphText(v: unknown, max: number): string {
  return typeof v === 'string'
    ? v.replace(/\r\n?/g, '\n').replace(/[\u0000-\u0009\u000b-\u001f]/g, ' ').replace(/[ \t]+/g, ' ').replace(/ *\n */g, '\n').replace(/\n{3,}/g, '\n\n').trim().slice(0, max)
    : '';
}

function firm(v: unknown): IFirm | undefined {
  if (!v || typeof v !== 'object') {
    return undefined;
  }
  const f = v as { staff?: unknown; programs?: unknown; listsReady?: unknown; instalments?: unknown; restricted?: unknown };
  const r = (f.restricted && typeof f.restricted === 'object' ? f.restricted : {}) as { driveId?: unknown; libraryName?: unknown; ready?: unknown };
  const rDrive: string = text(r.driveId, 200);
  const rReady: string = text(r.ready, 40);
  const listsReady: string = text(f.listsReady, 40);
  return {
    staff: list(f.staff, LIMITS.staff, s => {
      const x = (s ?? {}) as { name?: unknown; email?: unknown; role?: unknown };
      const name: string = text(x.name, 120);
      const email: string = text(x.email, 200);
      return name ? { name, email: /^[^@\s]+@[^@\s]+$/.test(email) ? email : undefined, role: x.role === 'lawyer' ? 'lawyer' : 'clerk' } : undefined;
    }),
    programs: list(f.programs, LIMITS.programs, p => (/^[a-z0-9-]{1,60}$/.test(text(p, 60)) ? text(p, 60) : undefined)),
    instalments: list(f.instalments, 10, i => {
      const x = (i ?? {}) as { label?: unknown; stage?: unknown };
      const label: string = text(x.label, 80);
      const stage: string = text(x.stage, 20);
      return label && STAGES.some(st => st.id === stage) ? { label, stage } : undefined;
    }),
    listsReady: listsReady && !isNaN(Date.parse(listsReady)) ? new Date(listsReady).toISOString() : undefined,
    restricted: /^[A-Za-z0-9!_-]{1,200}$/.test(rDrive) && rReady && !isNaN(Date.parse(rReady))
      ? { driveId: rDrive, libraryName: text(r.libraryName, 120) || 'Restricted files', ready: new Date(rReady).toISOString() }
      : undefined
  };
}

/** Whether automatic filing is paused right now (a timed pause ends on its own). */
export function isPaused(w: IWorkspace, now: Date = new Date()): boolean {
  return w.paused && (!w.pausedUntil || Date.parse(w.pausedUntil) > now.getTime());
}

export interface IWorkspaceStore {
  list(tenantId: string): Promise<IWorkspace[]>;
  get(tenantId: string, driveId: string): Promise<IWorkspace | undefined>;
  /**
   * Saves. With `etag`, only if nobody saved since that version was read (else WorkspaceConflictError);
   * returns the new etag.
   */
  put(tenantId: string, workspace: IWorkspace, etag?: string): Promise<string | undefined>;
}

export class WorkspaceConflictError extends Error {
  constructor() {
    super('Someone else changed these settings a moment ago. Reload to see their changes, then try again.');
  }
}

export class WorkspaceTooLargeError extends Error {
  constructor() {
    super('These settings are too large to save. Remove some clients or learned rules and try again.');
  }
}

/**
 * Table Storage holds at most 64 KB (32K characters) per text property and 1 MB per row, so the
 * settings are split over properties d0, d1, … (a firm with a few thousand clients still fits).
 */
const CHUNK: number = 30000;
const MAX_CHUNKS: number = 30;

export function toChunks(workspace: Omit<IWorkspace, 'etag'>): { [key: string]: string | number } {
  const data: string = JSON.stringify(workspace);
  const count: number = Math.ceil(data.length / CHUNK);
  if (count > MAX_CHUNKS) {
    throw new WorkspaceTooLargeError();
  }
  const out: { [key: string]: string | number } = { chunks: count };
  for (let i = 0; i < count; i++) {
    out[`d${i}`] = data.slice(i * CHUNK, (i + 1) * CHUNK);
  }
  return out;
}

export function fromChunks(e: Record<string, unknown>): IWorkspace {
  const count: number = Number(e.chunks) || 0;
  let data: string = '';
  for (let i = 0; i < count; i++) {
    data += String(e[`d${i}`] ?? '');
  }
  return JSON.parse(data) as IWorkspace;
}

/** Row keys can't contain / \ # ? — drive ids don't, but be sure. */
export function rowKey(id: string): string {
  return id.replace(/[/\\#?]/g, '_');
}

/** In memory, for tests. */
export class MemoryWorkspaceStore implements IWorkspaceStore {
  private readonly _data: { [key: string]: IWorkspace } = {};

  public async list(tenantId: string): Promise<IWorkspace[]> {
    return Object.keys(this._data).filter(k => k.indexOf(`${tenantId}|`) === 0).map(k => this._data[k]);
  }

  public async get(tenantId: string, driveId: string): Promise<IWorkspace | undefined> {
    return this._data[`${tenantId}|${driveId}`];
  }

  private _version: number = 0;

  public async put(tenantId: string, workspace: IWorkspace, etag?: string): Promise<string | undefined> {
    const key: string = `${tenantId}|${workspace.driveId}`;
    if (etag && this._data[key]?.etag !== etag) {
      throw new WorkspaceConflictError();
    }
    const next: string = `v${++this._version}`;
    this._data[key] = { ...JSON.parse(JSON.stringify(workspace)), etag: next };
    return next;
  }
}
